import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download, FileSpreadsheet, Search, SlidersHorizontal } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Alert } from '@/components/ui/Alert';
import { DatePicker } from '@/components/ui/DatePicker';
import { SearchableMultiSelect, type SearchableMultiSelectOption } from '@/components/ui/SearchableMultiSelect';
import { getFilterOptions } from '@/services/filters.service';
import { saveBlob } from '@/services/standardReports.service';
import {
  FlexReportRefusal,
  exportExcel,
  fetchFacts,
  type FlexFilterSelection,
} from '@/services/marketingFlexTable.service';
import type { DateRangePresetKey } from '@/lib/dateRangePresets';
import { cn } from '@/lib/utils';
import { DEFAULT_METRICS, type MetricKey } from './flexCatalog';
import { buildGrid } from './pivot';
import { buildCsv, buildExcelPayload, exportFileName, type FlexExportFilter } from './flexExport';
import { FLEX_PRESETS, presetPeriod, useFlexSetup } from './useFlexSetup';
import { DimensionBuilder } from './DimensionBuilder';
import { FlexPivotTable } from './FlexPivotTable';
import { MetricsSelectorModal } from './MetricsSelectorModal';

/** The custom-range escape hatch, kept out of the preset list itself (as the runner). */
const CUSTOM = '__custom__';

export const FLEX_COPY = {
  noMetrics: 'Choose at least one metric to show.',
  noData: 'No activity for this period and these filters.',
  noSearchMatch: 'No rows match your search.',
  loadFailed: "The report couldn't load. Try again, and if it keeps happening contact support.",
  exportFailed: "The file couldn't be created. Try again, and if it keeps happening contact support.",
  choosePeriod: 'Choose a From and To date.',
};

const PATIENT_TYPE_OPTIONS: SearchableMultiSelectOption[] = [
  { value: '1', label: 'New' },
  { value: '2', label: 'Existing' },
];

/**
 * Marketing Flex Table (R1–R28).
 *
 * The facts for one period and filter set are fetched once; every other
 * control — dimensions, view, metrics, sort, search, expand — re-pivots them
 * in the browser through the pure engine in `pivot.ts`, so only a period or
 * filter change reloads (R7). The table, the CSV and the Excel file all read
 * the same grid, so the on-screen view and both files cannot drift (R26).
 *
 * The wrapper, back link, header and filter bar are the runner's
 * (StandardReportRunnerPage.tsx), copied rather than shared — extracting them
 * is deferred follow-up work, and the runner is not touched here.
 *
 * The Marketing section opens the same page as its Marketing Dashboard
 * (docs/plans/2026-11-03): only the title and the back link differ, and the
 * remembered setup is the same one, since it is keyed on the page, not the
 * route.
 */
export default function MarketingFlexTablePage({ entry = 'reports' }: { entry?: 'reports' | 'marketing' }) {
  const navigate = useNavigate();
  const { setup, update, ready } = useFlexSetup();

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [search, setSearch] = useState('');
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [excelBusy, setExcelBusy] = useState(false);

  const { period, filters } = setup;
  const periodValid = period.from !== '' && period.to !== '';

  const { data: filterOptions } = useQuery({
    queryKey: ['marketing-flex-filter-options'],
    queryFn: () => getFilterOptions(['facility', 'tag']),
    staleTime: 5 * 60_000,
  });
  const facilityOptions: SearchableMultiSelectOption[] = filterOptions?.facility ?? [];
  const tagOptions: SearchableMultiSelectOption[] = filterOptions?.tag ?? [];

  // A remembered location or tag that is no longer offered (deactivated,
  // deleted) would keep filtering the table with no way to see or clear it,
  // so it is dropped once the lists are known. An empty list is not "nothing
  // is offered": the options call answers [] for a type whose lookup failed,
  // so an empty list leaves the remembered choice alone.
  useEffect(() => {
    if (!ready || !filterOptions) return;
    const keepOffered = (selected: string[], options: SearchableMultiSelectOption[] | undefined) => {
      if (!options || options.length === 0) return selected;
      const offered = new Set(options.map((o) => o.value));
      return selected.filter((v) => offered.has(v));
    };
    const facility = keepOffered(filters.facility, filterOptions.facility);
    const keptTags = keepOffered(filters.tags, filterOptions.tag);
    if (facility.length !== filters.facility.length || keptTags.length !== filters.tags.length) {
      update((prev) => ({ ...prev, filters: { ...prev.filters, facility, tags: keptTags } }));
    }
  }, [ready, filterOptions, filters.facility, filters.tags, update]);

  const sortedKey = (values: string[]) => [...values].sort();
  const factsQuery = useQuery({
    queryKey: [
      'marketing-flex-facts',
      period.from,
      period.to,
      sortedKey(filters.facility),
      sortedKey(filters.tags),
      sortedKey(filters.patientType),
    ],
    queryFn: () => fetchFacts({ from: period.from, to: period.to }, filters),
    // Wait for the remembered setup, or the defaults would be fetched and then thrown away.
    enabled: ready && periodValid,
    placeholderData: keepPreviousData,
    retry: false,
    // R7: only a period or filter change reloads. Not cached across changes, so
    // returning to an earlier filter set (Reset) loads it fresh.
    refetchOnWindowFocus: false,
  });
  const facts = factsQuery.data;
  // A reload with the previous grid still showing: dimmed, and nothing exported from it.
  const refreshing = factsQuery.isFetching && !!facts;
  const exportReady = !!facts && !factsQuery.isPlaceholderData && !factsQuery.isFetching && !factsQuery.isError;

  // The box updates at once; the table regroups on the deferred value, so
  // typing never waits on a large table.
  const deferredSearch = useDeferredValue(search);
  const grid = useMemo(
    () =>
      facts
        ? buildGrid(
            facts,
            {
              primary: setup.primary,
              secondary: setup.secondary,
              view: setup.view,
              metrics: setup.metrics,
              sort: setup.sort,
            },
            // Both types selected is the same as no filter.
            { expanded, search: deferredSearch, onePatientType: new Set(setup.filters.patientType).size === 1 },
          )
        : null,
    [facts, setup.primary, setup.secondary, setup.view, setup.metrics, setup.sort, setup.filters.patientType, expanded, deferredSearch],
  );

  const noData =
    !!facts &&
    facts.leads.length === 0 &&
    facts.appointments.length === 0 &&
    facts.newPatients.length === 0 &&
    facts.payments.length === 0 &&
    (facts.newCustomers ?? []).length === 0;

  const periodOptions = useMemo(
    () => [
      ...FLEX_PRESETS.map((p) => ({ value: p.key as string, label: p.label })),
      { value: CUSTOM, label: 'Custom range…' },
    ],
    [],
  );

  const setFilters = (patch: Partial<FlexFilterSelection>) =>
    update((prev) => ({ ...prev, filters: { ...prev.filters, ...patch } }));

  const activeFilterCount = [filters.facility, filters.patientType, filters.tags].filter((v) => v.length > 0).length;

  const exportFilters = (): FlexExportFilter[] => {
    const labelled = (label: string, values: string[], options: SearchableMultiSelectOption[]) => ({
      label,
      values: values.map((v) => options.find((o) => o.value === v)?.label ?? v),
    });
    return [
      labelled('Location', filters.facility, facilityOptions),
      labelled('Patient Type', filters.patientType, PATIENT_TYPE_OPTIONS),
      labelled('Patient Tags', filters.tags, tagOptions),
    ].filter((f) => f.values.length > 0);
  };

  const exportContext = () => ({ period: { from: period.from, to: period.to }, filters: exportFilters() });

  const handleCsv = () => {
    if (!grid) return;
    setDownloadError(null);
    const csv = buildCsv(grid, exportContext());
    // The byte-order mark makes Excel read the file as UTF-8 (as lib/orderExport.ts does).
    saveBlob(new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' }), exportFileName(period, 'csv'));
  };

  const handleExcel = async () => {
    if (!grid) return;
    setDownloadError(null);
    setExcelBusy(true);
    try {
      const fileName = exportFileName(period, 'xlsx');
      const blob = await exportExcel({ ...buildExcelPayload(grid, exportContext()), fileName });
      saveBlob(blob, fileName);
    } catch (e) {
      setDownloadError(e instanceof FlexReportRefusal ? e.message : FLEX_COPY.exportFailed);
    } finally {
      setExcelBusy(false);
    }
  };

  const toggleRow = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const applyMetrics = (metrics: MetricKey[]) =>
    update((prev) => ({
      ...prev,
      metrics,
      sort: prev.sort && prev.sort.by !== 'dimension' && !metrics.includes(prev.sort.by) ? null : prev.sort,
    }));

  const refusal = factsQuery.error instanceof FlexReportRefusal ? factsQuery.error.message : null;

  const renderBody = () => {
    if (!periodValid) return <p className="py-8 text-center text-sm text-slate-500">{FLEX_COPY.choosePeriod}</p>;
    if (factsQuery.isError) {
      if (refusal) return <Alert variant="warning">{refusal}</Alert>;
      return (
        <Alert variant="error">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{FLEX_COPY.loadFailed}</span>
            <Button variant="secondary" size="sm" onClick={() => void factsQuery.refetch()}>
              Try again
            </Button>
          </div>
        </Alert>
      );
    }
    if (!facts || !grid) return <p className="py-8 text-center text-sm text-slate-500">Loading…</p>;
    if (setup.metrics.length === 0) {
      return <p className="py-8 text-center text-sm text-slate-500">{FLEX_COPY.noMetrics}</p>;
    }
    if (noData && !refreshing) {
      return <p className="py-8 text-center text-sm text-slate-500">{FLEX_COPY.noData}</p>;
    }
    return (
      <>
        {refreshing && <p className="mb-2 text-sm text-slate-500">Loading…</p>}
        <div className={cn('transition-opacity', refreshing && 'pointer-events-none opacity-50')} aria-busy={refreshing}>
          <FlexPivotTable
            grid={grid}
            sort={setup.sort}
            onSortChange={(sort) => update({ sort })}
            onToggle={toggleRow}
          />
          {grid.rows.length === 0 && deferredSearch.trim() !== '' && setup.primary !== null && (
            <p className="py-6 text-center text-sm text-slate-500">{FLEX_COPY.noSearchMatch}</p>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 p-6">
        {entry === 'reports' && (
          <button
            type="button"
            onClick={() => navigate('/reports')}
            className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
          >
            <ArrowLeft className="h-4 w-4" />
            New Report
          </button>
        )}

        <PageHeader
          variant="compact"
          title={entry === 'marketing' ? 'Marketing Dashboard' : 'Marketing Flex Table'}
          actions={
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Download className="h-4 w-4" />}
                onClick={handleCsv}
                disabled={!exportReady || excelBusy}
              >
                Download CSV
              </Button>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<FileSpreadsheet className="h-4 w-4" />}
                onClick={() => void handleExcel()}
                disabled={!exportReady}
                loading={excelBusy}
              >
                Download Excel
              </Button>
            </div>
          }
        />

        <p className="mb-4 max-w-3xl text-sm text-slate-500">
          Leads, appointments, new patients and revenue, grouped and ordered the way you choose.
        </p>

        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-4">
          <div className="w-56">
            <Select
              label="Period"
              options={periodOptions}
              value={period.presetKey ?? CUSTOM}
              onChange={(v) => {
                const key = (v as string | null) ?? null;
                if (key === null) return;
                setExpanded(new Set());
                if (key === CUSTOM) update((prev) => ({ ...prev, period: { ...prev.period, presetKey: null } }));
                else update({ period: presetPeriod(key as DateRangePresetKey) });
              }}
            />
          </div>

          {period.presetKey === null ? (
            <>
              <div className="w-44">
                <DatePicker
                  id="flex-table-from"
                  label="From"
                  value={period.from}
                  onChange={(from) => update((prev) => ({ ...prev, period: { ...prev.period, from } }))}
                />
              </div>
              <div className="w-44">
                <DatePicker
                  id="flex-table-to"
                  label="To"
                  value={period.to}
                  onChange={(to) => update((prev) => ({ ...prev, period: { ...prev.period, to } }))}
                />
              </div>
            </>
          ) : (
            period.from && (
              <span className="pb-2 text-xs text-slate-500">
                {period.from} → {period.to}
              </span>
            )
          )}

          <div className="w-64">
            <label htmlFor="flex-table-locations" className="mb-1 block text-sm font-medium text-slate-700">
              Location
            </label>
            <SearchableMultiSelect
              id="flex-table-locations"
              options={facilityOptions}
              selectedValues={filters.facility}
              onSelectionChange={(facility) => setFilters({ facility })}
              placeholder="All locations"
              searchPlaceholder="Search locations…"
              emptyMessage="No locations found"
            />
          </div>

          <div className="w-64">
            <label htmlFor="flex-table-patient-types" className="mb-1 block text-sm font-medium text-slate-700">
              Patient Type
            </label>
            <SearchableMultiSelect
              id="flex-table-patient-types"
              options={PATIENT_TYPE_OPTIONS}
              selectedValues={filters.patientType}
              onSelectionChange={(patientType) => setFilters({ patientType })}
              placeholder="New and existing"
              searchPlaceholder="Search patient types…"
              emptyMessage="No patient types found"
            />
          </div>

          <div className="w-64">
            <label htmlFor="flex-table-tags" className="mb-1 block text-sm font-medium text-slate-700">
              Patient Tags
            </label>
            <SearchableMultiSelect
              id="flex-table-tags"
              options={tagOptions}
              selectedValues={filters.tags}
              onSelectionChange={(tags) => setFilters({ tags })}
              placeholder="All tags"
              searchPlaceholder="Search tags…"
              emptyMessage="No tags found"
            />
          </div>

          {activeFilterCount > 0 && (
            <div className="flex items-center gap-2 pb-1.5">
              <span className="text-xs text-slate-500">
                {activeFilterCount === 1 ? '1 filter' : `${activeFilterCount} filters`}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFilters({ facility: [], tags: [], patientType: [] })}
              >
                Reset
              </Button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <DimensionBuilder
            primary={setup.primary}
            secondary={setup.secondary}
            view={setup.view}
            onChange={({ primary, secondary }) => {
              setExpanded(new Set());
              update({ primary, secondary });
            }}
            onViewChange={(view) => update({ view })}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<SlidersHorizontal className="h-4 w-4" />}
              onClick={() => setMetricsOpen(true)}
            >
              Metrics
            </Button>
            <Button variant="ghost" size="sm" onClick={() => applyMetrics([...DEFAULT_METRICS])}>
              Reset Columns
            </Button>
            <div className="relative w-56">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                aria-label="Search rows"
                placeholder="Search rows…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-full rounded-md border border-slate-300 pl-8 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {downloadError && (
          <div className="mt-4">
            <Alert variant="error">{downloadError}</Alert>
          </div>
        )}
      </div>

      {/* The side padding sits outside the scroller, so nothing scrolls through the gap beside the pinned column. */}
      <div className="flex min-h-0 flex-1 flex-col px-6 pb-6">
        <div className="min-h-0 flex-1 overflow-auto">{renderBody()}</div>
      </div>

      {metricsOpen && (
        <MetricsSelectorModal
          selected={setup.metrics}
          onApply={applyMetrics}
          onClose={() => setMetricsOpen(false)}
        />
      )}
    </div>
  );
}
